/* eslint-disable react/react-in-jsx-scope */
import { CircularProgress, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Suspense } from 'react';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';
import { formatMoneyAmount } from '../components/AryNumberFormatter';
import AryProfileIdToImage from '../components/AryProfileIdToImage';
import { GET_TICKETS_FOR_EXCHANGER } from '../constants/aryGraphqlSchemas';
import arySystemSettings from '../constants/arySystemSettings';
import {
  getTimeZoneFormattedDate,
  getTimeZoneFormattedTime,
  limitCharacters,
  translatePhraseItem
} from '../utils/Utils';
import useCurrencyDecimalPlaces from './useCurrencyDecimalPlaces';
import useShowCountryName from './useShowCountryName';
import useShowCurrency from './useShowCurrency';
import useShowSystemCurrency from './useShowSystemCurrency';
import useSystemSettingsContext from './useSystemSettingsContext';
import useTemplateParsing from './useTemplateParsing';

export default function useTableDataParser() {
  const theme = useTheme();
  const showCurrency = useShowCurrency();
  const showSystemCurrency = useShowSystemCurrency();
  const showCountryName = useShowCountryName();
  const parseDescription = useTemplateParsing();
  const { systemSettings } = useSystemSettingsContext();
  const [getCurrencyDecimalPlaces] = useCurrencyDecimalPlaces();
  const { t } = useFrameworkTranslation();

  return {
    // Text Parser
    rowNumberParser: (row, key) => row[key],
    // textParser: (row, key) => row[key],
    textParser: (row, key) => row[key],
    textTranslatedParser: (row, key) => t(row[key]),
    descriptionParser: (row, key) => (
      <Tooltip
        title={
          <Typography variant="c3Bold">
            {parseDescription(row[key], row.metadata)}
          </Typography>
        }
        componentsProps={{
          tooltip: {
            sx: {
              background: theme.tooltip.label.color,
              boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
              width: 'auto',
              textAlign: 'center',
              padding: '10px 7px'
            }
          }
        }}
      >
        <span>
          {limitCharacters(parseDescription(row[key], row.metadata), 60) || '-'}
        </span>
      </Tooltip>
    ),
    // Date Parser
    dateParser: (row, key) =>
      getTimeZoneFormattedDate(
        row[key],
        systemSettings[arySystemSettings.isGregorian],
        systemSettings[arySystemSettings.timeZone]
      ),
    dateTimeParser: (row, key) =>
      getTimeZoneFormattedTime(
        row[key],
        systemSettings[arySystemSettings.isGregorian],
        systemSettings[arySystemSettings.timeZone]
      ),
    dateLatinParser: (row, key) =>
      getTimeZoneFormattedDate(
        row[key],
        systemSettings[arySystemSettings.isGregorian],
        systemSettings[arySystemSettings.timeZone],
        systemSettings[arySystemSettings.isDigitsLatin]
      ),
    dateGregorianParser: (row, key) =>
      getTimeZoneFormattedDate(
        row[key],
        true,
        systemSettings[arySystemSettings.timeZone],
        systemSettings[arySystemSettings.isDigitsLatin]
      ),
    // Money PArser
    moneyFormatParser: (row, key) => formatMoneyAmount(row[key]),
    moneyFormatByTitleParser: (row, key) => {
      const decimalPlaces = getCurrencyDecimalPlaces({
        title: row[key]
      });
      return formatMoneyAmount(row[key], decimalPlaces);
    },
    moneyFormatByCodeParser: (row, key, amount) => {
      const decimalPlaces = getCurrencyDecimalPlaces({
        code: row[key]
      });
      return row[amount] ? formatMoneyAmount(row[amount], decimalPlaces) : '0';
    },
    balanceMoneyFormatterParser: (row, key) => {
      const decimalPlaces = getCurrencyDecimalPlaces({
        title: row[key]
      });
      return (
        <span
          style={{
            color: row.isDebtor ? theme.text.red.color : theme.text.green.color
          }}
        >
          {formatMoneyAmount(row[key], decimalPlaces)}
        </span>
      );
    },
    countryNameParser: (row, key) => showCountryName({ countryCode: row[key] }),
    chequeMoneyFormatParser: (row, key) =>
      formatMoneyAmount(
        row[key],
        getCurrencyDecimalPlaces({
          title: row.currencyTitle
        })
      ),
    // Currency Parser
    showCurrencyByTitleParser: (row, key) => showCurrency({ title: row[key] }),
    showCurrencyByCodeParser: (row, key) => showCurrency({ code: row[key] }),
    showSystemCurrencyByTitleParser: (row, key) =>
      showSystemCurrency({ title: row[key] }),
    showSystemCurrencyByCodeParser: (row, key) =>
      showSystemCurrency({ code: row[key] }),
    // Status Parser
    statusParser: (row, key) => {
      const statusColor = row[key]
        ? theme.text.red.color
        : theme.text.green.color;

      const statusText = row[key] ? t('Debtor') : t('Creditor');

      return (
        <span key={key} style={{ color: statusColor }}>
          {statusText}
        </span>
      );
    },
    ticketStatusParser: (row, key) => {
      const TICKET_STATUSES = [
        {
          code: GET_TICKETS_FOR_EXCHANGER.ticketStatus.NEW,
          title: t('New'),
          color: theme.text.orange.color
        },
        {
          code: GET_TICKETS_FOR_EXCHANGER.ticketStatus.IN_PROGRESS,
          title: t('Under Review'),
          color: theme.text.blue.color
        },
        {
          code: GET_TICKETS_FOR_EXCHANGER.ticketStatus.REJECTED,
          title: t('Failed'),
          color: theme.text.red.color
        },
        {
          code: GET_TICKETS_FOR_EXCHANGER.ticketStatus.DONE,
          title: t('Done'),
          color: theme.text.green.color
        }
      ];

      function getTicketStatus(title) {
        return TICKET_STATUSES.find((status) => title === status.title);
      }

      const ticketStatusObj = getTicketStatus(row[key]);

      return (
        <Typography variant="t2Bold" style={{ color: ticketStatusObj.color }}>
          {ticketStatusObj.title}
        </Typography>
      );
    },
    // Name And Title Parser
    buyerFullNameParser: (row, key) => {
      const translatedFullName = translatePhraseItem(t, {
        text: row[key],
        shouldTranslate: row.buyerNameShouldTranslate
      });

      return <span>{translatedFullName}</span>;
    },
    sellerFullNameParser: (row, key) => {
      const translatedFullName = translatePhraseItem(t, {
        text: row[key],
        shouldTranslate: row.sellerNameShouldTranslate
      });

      return <span>{translatedFullName}</span>;
    },
    accountNameParser: (row, key) => {
      const translatedAccountName = translatePhraseItem(t, {
        text: row[key],
        shouldTranslate: row.accountNameShouldTranslate
      });

      return <span>{translatedAccountName}</span>;
    },
    groupTitleParser: (row, key) => <span>{row[key].join(' - ') || '-'}</span>,
    ticketTitleParser: (row, key) => (
      <span>{row[key] ? t(row[key]) : '-'}</span>
    ),
    // TransactionType Parser
    currencyTransactionType: (row, key) => (
      <span>
        {row[key] ? t('transactionType.buy') : t('transactionType.sell')}
      </span>
    ),
    transactionTypeParser: (row, key, transactionTypeDictionary) => (
      <span>{transactionTypeDictionary[row[key]] || '-'}</span>
    ),
    // ProfileId To Image PArser
    profileIdToImageParser: (row, key, size) => (
      <Suspense fallback={<CircularProgress size={25} />}>
        <AryProfileIdToImage
          profileImageId={row[key]}
          fullName={row.fullName}
          size={size}
        />
      </Suspense>
    ),
    chequePlaceParser: (row, key) =>
      translatePhraseItem(t, {
        text: row[key],
        shouldTranslate: row.shouldCurrentChequePlaceTranslate
      }),
    chequeIsDebtor: (row, key) => (row[key] ? t('Received') : t('Paid'))
  };
}
